import { networkActions } from 'actions/network';
import { assetActions } from 'actions/asset';
import { accidentActions } from 'actions/accident';
import { surveyActions } from 'actions/survey';
import { schemeActions } from 'actions/scheme';
import { performanceActions } from 'actions/performance';
import type {
  ResolvableAttributeDetailCategory,
  ResolvedAttributeDetails,
  AttributeModalDetails,
} from './attributeDetailsTypes';
import { isAttributeDetailCategory } from './attributeDetailsTypes';

export type { ResolvedAttributeDetails, AttributeModalDetails } from './attributeDetailsTypes';

export interface AttributesClickData {
  readonly category?: string;
  readonly id?: string | number;
  readonly name?: string;
  readonly Id?: string | number;
  readonly survey_type?: string;
  readonly survey_slug?: string;
}

/* Map click / legend payload passed from Dashboard into the Attributes modal. */
export interface AttributesModalData extends AttributesClickData {
  readonly latitude?: number;
  readonly longitude?: number;
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function readId(value: unknown): string | number | undefined {
  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }
  return undefined;
}

function readNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function normalizeCategory(value: string | undefined): string | undefined {
  if (value === 'Assets' || value === 'asset') {
    return 'asset';
  }
  return value;
}

function normalizeModalAssetId(
  id: string | number | undefined,
  altId: string | number | undefined,
): number | undefined {
  return toNumberId(id) ?? toNumberId(altId) ?? undefined;
}

/* Narrow ArcGIS / legend feature attributes into modal input. */
export function toAttributesModalData(
  source: Record<string, unknown>,
): AttributesModalData {
  const id = readId(source.id);
  const Id = readId(source.Id);

  return {
    category: normalizeCategory(readString(source.category)),
    id: normalizeModalAssetId(id, Id),
    name: readString(source.name),
    Id,
    survey_type: readString(source.survey_type),
    survey_slug: readString(source.survey_slug),
    latitude: readNumber(source.latitude),
    longitude: readNumber(source.longitude),
  };
}

export function enrichAttributesModalDataForAssetLayer(
  data: AttributesModalData,
  options?: { readonly isAssetLayer?: boolean },
): AttributesModalData {
  if (!options?.isAssetLayer) {
    return data;
  }

  const normalizedId =
    typeof data.id === 'number' ? data.id : toNumberId(data.id ?? data.Id);

  return {
    ...data,
    category: 'asset',
    ...(normalizedId !== null ? { id: normalizedId } : {}),
  };
}

export function isAssetHubModalData(
  data: unknown,
): data is AttributesModalData & { readonly id: number } {
  return (
    typeof data === 'object' &&
    data !== null &&
    typeof (data as { readonly id?: unknown }).id === 'number'
  );
}

function toNumberId(id: string | number | undefined): number | null {
  if (id === undefined || id === null || id === '') {
    return null;
  }
  const parsed = typeof id === 'number' ? id : Number(id);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  return Math.trunc(parsed);
}

function toStringId(id: string | number | undefined): string {
  return String(id ?? '');
}

function toSchemePayload(
  data: AttributesClickData,
): { readonly id: string | number } {
  return { id: data.id ?? data.Id ?? '' };
}

async function resolveByCategory(
  category: ResolvableAttributeDetailCategory,
  data: AttributesClickData,
  refreshData: boolean,
): Promise<ResolvedAttributeDetails | null> {
  switch (category) {
    case 'asset': {
      const assetId = toNumberId(data.id);
      if (assetId === null) {
        return null;
      }
      const details = await assetActions().getAssetDetails(assetId, refreshData);
      return { category: 'asset', details };
    }
    case 'accident': {
      const details = await accidentActions().getAccidentDetails(
        data.name ?? '',
        refreshData,
      );
      return { category: 'accident', details };
    }
    case 'survey': {
      const details = await surveyActions().getSurveyDetails(
        toStringId(data.id),
        refreshData,
      );
      return { category: 'survey', details };
    }
    case 'inspection': {
      const details = await surveyActions().getSurveyInspectionDetails(
        toStringId(data.id ?? data.Id),
        refreshData,
      );
      return { category: 'inspection', details };
    }
    case 'scheme': {
      const details = await schemeActions().getSchemeDetails(
        toSchemePayload(data),
        refreshData,
      );
      return { category: 'scheme', details };
    }
    case 'candidate schemes': {
      const details = await schemeActions().getFwpDetails(
        toStringId(data.id),
        refreshData,
      );
      return { category: 'candidate schemes', details };
    }
    case 'programmed scheme': {
      const details = await schemeActions().getProgrammedSchemeDetails(
        toSchemePayload(data),
        refreshData,
      );
      return { category: 'programmed scheme', details };
    }
    case 'treatment': {
      const details = await schemeActions().getTreatmentDetails(
        toStringId(data.id),
        refreshData,
      );
      return { category: 'treatment', details };
    }
    case 'performance': {
      const details = await performanceActions().getPerformanceDetails(
        toStringId(data.id),
        refreshData,
      );
      return { category: 'performance', details };
    }
    case 'network': {
      const details = await networkActions().getSectionDetails(
        data.name ?? '',
        refreshData,
      );
      return { category: 'network', details };
    }
    default: {
      const _exhaustive: never = category;
      return _exhaustive;
    }
  }
}

function canResolveNetworkSection(data: AttributesClickData): boolean {
  return (
    !data.survey_type &&
    !data.survey_slug &&
    typeof data.name === 'string' &&
    data.name.length > 0
  );
}

/*
  Resolve modal details for the Attributes panel.
  Returns null when data is not meant for Attributes.
 */
export async function resolveAttributeDetails(
  data: AttributesClickData,
  refreshData: boolean = false,
): Promise<ResolvedAttributeDetails | null> {
  const category = data.category;

  if (isAttributeDetailCategory(category)) {
    return resolveByCategory(category, data, refreshData);
  }

  // Unlabelled legend features fall back to network section lookup.
  if (canResolveNetworkSection(data)) {
    const details = await networkActions().getSectionDetails(data.name!, refreshData);
    return { category: 'network-fallback', details };
  }

  return null;
}

const EXPORT_HIDDEN_DETAIL_KEYS = new Set([
  'geometry',
  'attributes',
  'elements',
  'segmentable',
]);

function flattenDetailsForExport(
  details: AttributeModalDetails,
): Record<string, unknown> {
  const record = details as unknown as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    if (!EXPORT_HIDDEN_DETAIL_KEYS.has(key)) {
      out[key] = value;
    }
  }
  return out;
}

/*
  Loads full attribute fields for shapefile export (same sources as the Attributes modal).
 */
export async function resolveExportAttributesForFeature(
  data: AttributesClickData,
  refreshData: boolean = false,
): Promise<Record<string, unknown> | null> {
  const resolved = await resolveAttributeDetails(data, refreshData);
  if (!resolved) {
    return null;
  }

  const flat = flattenDetailsForExport(resolved.details);

  if (resolved.category === 'asset') {
    const assetId = toNumberId(data.id);
    if (assetId !== null) {
      try {
        const extraAttributes = await assetActions().getAssetAttributes(
          assetId,
          refreshData,
        );
        for (const entry of extraAttributes) {
          if (entry.name) {
            flat[entry.name] = entry.value;
          }
        }
      } catch {
        // Extended attributes are optional for export.
      }
    }
  }

  return flat;
}
